import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fallbackLng } from 'i18n-settings';
import { useCallback } from 'react';

import {
  createSequenceService,
  deleteSequenceService,
  instructionFeedbackService,
  patchSequenceService,
  synthesizeSpeechService,
  translateSequenceService,
  updateSequenceService,
} from '@/services/httpServices/SequenceServices';
import type {
  CreateSequenceServiceConfig,
  DeleteSequenceServiceConfig,
  GetSequenceByIdServiceConfig,
  InstructionFeedbackServiceConfig,
  PatchSequenceServiceConfig,
  SequenceChangelogServiceConfig,
  SynthesizeSpeechServiceConfig,
  TranslateSequenceServiceConfig,
  UpdateSequenceServiceConfig,
} from '@/services/httpServices/SequenceServices/types';
import { getCurrentLocaleClient } from '@/utils/language-helpers';

import { sequenceByIdKeys, sequenceChangelogKeys, sequenceListKeys } from '../queries/sequenceQueryKeys';

/**
 * Custom hook to handle sequence language changes efficiently
 * This uses the queryClient directly instead of passing callbacks
 */
export function useSequenceLanguageChange() {
  const queryClient = useQueryClient();
  // Change language without translation. Both the path and query language track the
  // in-form picker selection — UI locale never leaks into the content request here.
  const changeLanguage = useCallback(
    async (sequenceQueryParams: GetSequenceByIdServiceConfig, newLanguage: string): Promise<void> => {
      if (sequenceQueryParams === undefined || sequenceQueryParams.params === undefined) {
        return;
      }

      const updatedParams: GetSequenceByIdServiceConfig = {
        ...sequenceQueryParams,
        params: {
          ...sequenceQueryParams.params,
          params: {
            ...sequenceQueryParams.params.params,
            sequenceLanguage: newLanguage,
          },
          query: {
            language: newLanguage,
          },
        },
      };

      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(updatedParams),
      });
    },
    [queryClient],
  );

  return { changeLanguage };
}

export function useCreateSequenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateSequenceServiceConfig) => {
      try {
        const response = await createSequenceService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async () => {
      // Invalidate all sequence list queries
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });

      // No need to invalidate a specific sequence by ID since this is a new sequence
    },
  });
}

export function useCreateSequenceMutationWithoutInvalidation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: CreateSequenceServiceConfig) => {
      try {
        const response = await createSequenceService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (data) => {
      // Invalidate sequence list queries to include the new sequence
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });

      // Return the data so the component can access the newly created sequence ID
      return data;
    },
  });
}

export function useUpdateSequenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: UpdateSequenceServiceConfig) => {
      try {
        const response = await updateSequenceService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all sequence list queries
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });

      // Invalidate the specific sequence by ID query
      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(variables),
      });

      // Invalidate sequence changelog queries for this sequence
      await queryClient.invalidateQueries({
        queryKey: sequenceChangelogKeys(),
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Check if this is a sequence changelog query for the same sequence
          return (
            Array.isArray(queryKey)
            && queryKey[0] === 'sequenceChangelog'
            && queryKey[1]?.params?.params?.id === variables.params?.params.id // sequenceId is in the params
          );
        },
      });
    },
  });
}

export function useDeleteSequenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: DeleteSequenceServiceConfig) => {
      try {
        await deleteSequenceService(config);
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Only invalidate sequence list queries - don't invalidate the specific sequence
      // that was just deleted, as this causes unnecessary refetches
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });

      // Invalidate sequence changelog queries for this sequence
      await queryClient.invalidateQueries({
        queryKey: sequenceChangelogKeys(),
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey)
            && queryKey[0] === 'sequenceChangelog'
            && queryKey[1]?.params?.params?.id === variables.params?.params.id // sequenceId is in the params
          );
        },
      });
    },
  });
}

export function useTranslateSequenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: TranslateSequenceServiceConfig) => {
      try {
        const response = await translateSequenceService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate all sequence list queries
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });

      // Invalidate the specific sequence by ID query
      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(variables),
      });

      // Invalidate sequence changelog queries for this sequence
      await queryClient.invalidateQueries({
        queryKey: sequenceChangelogKeys(),
        predicate: (query) => {
          const queryKey = query.queryKey;
          return (
            Array.isArray(queryKey)
            && queryKey[0] === 'sequenceChangelog'
            && queryKey[1]?.params?.params?.id === variables.params?.params.id // sequenceId is in the params
          );
        },
      });
    },
  });
}

export function useSynthesizeSpeechMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: SynthesizeSpeechServiceConfig) => {
      try {
        const response = await synthesizeSpeechService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Invalidate the specific sequence by ID query if needed
      // This may not be required for synthesize speech since it doesn't change the sequence data
      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(variables),
      });
    },
  });
}

export function useInstructionFeedbackMutation() {
  return useMutation({
    mutationFn: async (config: InstructionFeedbackServiceConfig) => {
      try {
        const response = await instructionFeedbackService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
  });
}

export function usePatchSequenceVoiceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: PatchSequenceServiceConfig) => {
      try {
        const response = await patchSequenceService(config);
        return response;
      } catch (error) {
        throw new Error(
          error instanceof Error ? error.message : 'Unknown error',
        );
      }
    },
    onSuccess: async (_, variables) => {
      // Build GET-compatible config for cache invalidation.
      // Mutation configs include `body` which doesn't exist in GET query keys,
      // causing partialMatchKey to fail when body is part of the filter key.
      const invalidationConfig: GetSequenceByIdServiceConfig = {
        httpRequestConfig: variables.httpRequestConfig,
        params: {
          params: {
            spaceId: variables.params?.params.spaceId ?? '',
            id: variables.params?.params.id ?? '',
            sequenceLanguage: variables.params?.params.sequenceLanguage ?? '',
          },
          query: variables.params?.query ?? { language: getCurrentLocaleClient() },
        },
      };

      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(invalidationConfig),
      });

      await queryClient.invalidateQueries({
        queryKey: sequenceChangelogKeys(invalidationConfig as SequenceChangelogServiceConfig),
      });
    },
  });
}

export function usePublishSequenceMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Omit<UpdateSequenceServiceConfig, 'params'> & {
      params: UpdateSequenceServiceConfig['params'] & {
        body?: null;
      };
    }) => {
      const publishConfig: UpdateSequenceServiceConfig = {
        ...config,
        params: {
          ...config.params,
          query: {
            ...config.params.query,
            additionalAction: 'publish',
          },
          body: null,
        },
      };
      return updateSequenceService(publishConfig);
    },
    onSuccess: async (_, variables) => {
      // Invalidate sequence-related queries
      await queryClient.invalidateQueries({
        queryKey: sequenceListKeys(),
      });
      await queryClient.invalidateQueries({
        queryKey: sequenceByIdKeys(variables),
      });

      // Invalidate validation queries specifically for this unit
      const { unitValidationKeys } = await import('../queries/courseQueryKeys');
      const unitId = variables.params?.params?.id;
      const spaceId = variables.params?.params?.spaceId;
      if (unitId && spaceId) {
        // Create a validation config to target this specific unit
        const validationConfig = {
          httpRequestConfig: variables.httpRequestConfig,
          params: {
            params: {
              spaceId,
            },
            body: {
              unitId,
              unitType: 'sequence' as const,
              language: variables.params?.query?.language || getCurrentLocaleClient() || fallbackLng,
            },
          },
        };

        // Only invalidate validation queries for this specific unit
        await queryClient.invalidateQueries({
          queryKey: unitValidationKeys(validationConfig),
        });
      }
    },
  });
}
